/**
 * Data hook for the KPI Scorecard readiness endpoint (backfill checklist).
 * Calls GET /api/pm/scorecard/readiness/. Fixture substitution (dev without a
 * running backend) only happens when VITE_SCORECARD_FIXTURE=true — a real
 * network error in production always surfaces as `error`, never as
 * fabricated data.
 *
 * Lazy by design — only fetched when the Readiness tab/panel is opened
 * (mirrors the pattern in useScorecardMetric.ts).
 */
import { useEffect, useState } from 'react';

import { scorecard as scorecardApi } from '@/helpers/ApiHelper';
import { SCORECARD_READINESS_FIXTURE } from '@/pages/scorecard/scorecard.fixture';
import type { ScorecardReadiness, ScorecardReadinessParams } from '@/types/scorecard';

const USE_FIXTURE = import.meta.env.VITE_SCORECARD_FIXTURE === 'true';

export function useScorecardReadiness(
  params: ScorecardReadinessParams = {},
  enabled = true,
) {
  const [data, setData] = useState<ScorecardReadiness | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    if (USE_FIXTURE) {
      const tid = setTimeout(() => {
        if (!cancelled) {
          setData(SCORECARD_READINESS_FIXTURE);
          setIsLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }

    scorecardApi
      .getScorecardReadiness(params)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          // Fixture substitution is gated on the explicit dev flag ONLY.
          // Never fall back silently in prod — a real network error (Cloud Run
          // cold start, transient CORS/backend blip, etc.) must surface as an
          // error with data left null, not render fabricated readiness data.
          if (USE_FIXTURE) {
            setData(SCORECARD_READINESS_FIXTURE);
            setError(null);
          } else {
            setError(e instanceof Error ? e : new Error(String(e)));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, params.preset, params.start, params.end, params.project]);

  return { data, isLoading, error };
}
